// Angular
import {
	Component,
	Output,
	EventEmitter,
	ViewChild,
	ViewContainerRef,
	ComponentFactoryResolver,
	OnInit,
	OnDestroy,
	Input
} from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
// Service
import {BroadcastService} from '../../service/broadcast.service';
// Component
import {DynamicContentDirective} from '../dynamic-component/dynamic-content.directive';
// Model
import {BroadcastEventModel} from '../../model/broadcast-event.model';
import {PageMetadataModel} from '../../model/page-metadata.model';
import {ROUTE_NAVIGATION_END_START, ROUTE_NAVIGATION_END_EVENT} from '../../model/routing.model';
// Other
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import { SubContent } from '../../model/sub-content.interface';

// Other
export const SHOW_SUB_HEADER_SECTION = 'showSubHeaderSection';

/**
 * A Dynamic Place Holder to load Custom Header Components injected from the Route
 */
@Component({
	selector: 'cv-breadcrumb',
	templateUrl: './breadcrumb.component.html',
	styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  @Input() isPWAMobile = false;
	@Output() toggleSubContentPanel: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild(DynamicContentDirective, { static: true }) dynamicContent: DynamicContentDirective;
	@ViewChild('breadcrumbSubDetail', { static: false, read: DynamicContentDirective }) breadcrumbSubDetail: DynamicContentDirective;

	private atComponentDestroy = new Subject();
	// Show hide the Sub Component, show by default
	public pageMetadata: PageMetadataModel = {
		subComponentShow: null,
		title: '',
		component: null,
		breadcrumb: true
	};
	private currentViewContainerRef: ViewContainerRef;
	private currentSubContentInstance: SubContent;
	public breadcrumbClassName = '';

	constructor(
		private broadcastService: BroadcastService,
		private router: Router,
		private activatedRoute: ActivatedRoute
	) {
		this.registerRouteEndListener();
	}

	ngOnInit(): void {
		this.currentViewContainerRef = this.dynamicContent.viewContainerRef;
	}

	/**
	 * Emit the opening of the SubContent Panel by clicking on the Breadcrumb Icon to the Right
	 * @param isOpen
	 */
	public onToggleSubContentPanel(isOpen: boolean = null): void {
		this.toggleSubContentPanel.emit(isOpen);
	}

	/**
	 * Register Route End Listener to handle the breadcrumb content change.
	 */
	private registerRouteEndListener(): void {
		this.broadcastService
			.onEvent(ROUTE_NAVIGATION_END_START)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(() => {
				// Clear current Context
				this.currentViewContainerRef.clear();
				this.pageMetadata.subComponentShow = null;
				this.pageMetadata.title = '';
				this.pageMetadata.component = null;
				this.pageMetadata.breadcrumb = true;
			});
		this.broadcastService
			.onEvent(ROUTE_NAVIGATION_END_EVENT)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcast: BroadcastEventModel) => {

				this.breadcrumbClassName = 'breadcrumb-' + this.router.url.substring(1).split('/', 2).join('-');

				const routeSnapshot: ActivatedRouteSnapshot = broadcast.event.routeEvent.snapshot;
				if (routeSnapshot.firstChild && routeSnapshot.firstChild.data) {
					let subComponentShow = routeSnapshot.firstChild.data.subComponentShow;
					if (subComponentShow === undefined || subComponentShow === null ) {
						subComponentShow = (routeSnapshot.firstChild.children
							&& routeSnapshot.firstChild.children.length
							&& routeSnapshot.firstChild.firstChild.data.subComponentShow !== undefined)
							? routeSnapshot.firstChild.firstChild.data.subComponentShow : undefined;
					}
					if (typeof subComponentShow !== 'undefined') {
						this.pageMetadata.subComponentShow = subComponentShow;
						if (!this.pageMetadata.subComponentShow) {
							this.onToggleSubContentPanel(false);
						}
					}
					if (typeof routeSnapshot.firstChild.data.breadcrumb !== 'undefined') {
						this.pageMetadata.breadcrumb = routeSnapshot.firstChild.data.breadcrumb;
					}
					// If the component has a custom Breadcrumb, draw it, or it's children
					let breadcrumbComponent = routeSnapshot.firstChild.data.breadcrumbComponent;
					if (!breadcrumbComponent && !routeSnapshot.data.keepBreadcrumb) {
						breadcrumbComponent = (routeSnapshot.firstChild.children && routeSnapshot.firstChild.children.length)
							? routeSnapshot.firstChild.firstChild.data.breadcrumbComponent : null;
					}
					if ( breadcrumbComponent && routeSnapshot.data.componentFactoryResolver) {
						this.pageMetadata.component = breadcrumbComponent;
						// TODO: dontiveros - modify this to also consider the child route breadcrumb component metadata.
						this.pageMetadata.componentData = routeSnapshot.firstChild.data.breadcrumbComponentData;
						const componentFactoryResolver = routeSnapshot.data.componentFactoryResolver;
						this.renderComponent(
							this.pageMetadata.component,
							componentFactoryResolver,
							this.pageMetadata.componentData,
							routeSnapshot.firstChild.params
						);
					} else if(!routeSnapshot.data.keepBreadcrumb) {
						// If not, use the default title
						this.pageMetadata.title = broadcast.event.moduleInfo.subTitle;
					}
					// If sub component show not set yet by the route configuration, then default it to true (show it)
					if (this.pageMetadata.subComponentShow === null) {
						this.pageMetadata.subComponentShow = true;
					}
				}
			});

		this.broadcastService
			.onEvent(SHOW_SUB_HEADER_SECTION)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcast: BroadcastEventModel) => {
				// Got the Data
				const subDetail: any = {
					show: broadcast.event.show,
					data: broadcast.event.data,
					component: broadcast.event.component,
					componentFactoryResolver: broadcast.event.componentFactoryResolver
				};
				// Clear Previous Value
				this.breadcrumbSubDetail.viewContainerRef.clear();
				if (subDetail.show) {
					const componentFactory = subDetail.componentFactoryResolver.resolveComponentFactory(subDetail.component);
					const componentRef = this.breadcrumbSubDetail.viewContainerRef.createComponent(componentFactory);
					componentRef.instance['data'] = subDetail.data;
					componentRef.instance['showDetail'] = subDetail.show;
				}
			});
	}

	/**
	 * Create the component and clear previous data on it
	 * @param componentFactoryResolver
	 */
	private renderComponent(
		component: any,
		componentFactoryResolver: ComponentFactoryResolver,
		componentData: any,
		params: any
	): void {
		this.currentViewContainerRef.clear();
		const componentFactory = componentFactoryResolver.resolveComponentFactory(component);
		const componentRef = this.currentViewContainerRef.createComponent(componentFactory);
		this.currentSubContentInstance = <SubContent>componentRef.instance;
		this.currentSubContentInstance.routeParams = params;
		if (componentData) {
			this.currentSubContentInstance.data = componentData;
		}
	}

	/**
	 * Close all when the element is destroyed
	 */
	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
